'use client';

import React, { useState } from 'react';
import Link from 'next/link';

type Tier = 'student' | 'undergraduate' | 'premium' | 'seniors';

interface Plan {
  tier: Tier;
  label: string;
  price: number;
  description: string;
  features: string[];
  gradient: string;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    tier: 'student',
    label: 'Student',
    price: 55000,
    description: 'For students starting their growth journey',
    features: [
      'Voice chat with Rafeeq AI',
      'Daily motivational videos',
      'Habit tracking & streaks',
      'Prayer times & Qibla',
      '5-language support',
    ],
    gradient: 'from-emerald-500 to-teal-600',
  },
  {
    tier: 'undergraduate',
    label: 'Undergraduate',
    price: 75000,
    description: 'For undergraduates building their future',
    features: [
      'Everything in Student',
      'Academic Research Mode',
      'Extended conversation history',
      'Priority AI responses',
      'Weekly growth analytics',
    ],
    gradient: 'from-blue-500 to-indigo-600',
    badge: 'Popular',
  },
  {
    tier: 'premium',
    label: 'Premium',
    price: 110000,
    description: 'For professionals & entrepreneurs',
    features: [
      'Everything in Undergraduate',
      'Career Launchpad access',
      'Advanced voice integration',
      'Unlimited AI coaching',
      'Business goal tracking',
    ],
    gradient: 'from-amber-500 to-orange-600',
  },
  {
    tier: 'seniors',
    label: 'Seniors',
    price: 90000,
    description: 'Exclusive ibada guidance for elders',
    features: [
      'Personalised ibada guidance',
      'Private Scholar Q&A (ask any Sheikh)',
      'Record or type your question',
      'Answers delivered privately to you',
      'Curated Deen content for seniors',
      'Prayer & Dhikr reminders',
    ],
    gradient: 'from-purple-600 to-violet-700',
    badge: 'Seniors Only',
  },
];

type CheckoutStep = 'form' | 'waiting' | 'success' | 'failed';

interface UgandaCheckoutProps {
  plan: Plan;
  onSuccess: () => void;
  onCancel: () => void;
}

function UgandaCheckout({ plan, onSuccess, onCancel }: UgandaCheckoutProps) {
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState<CheckoutStep>('form');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('Please enter your mobile money phone number.');
      return;
    }

    const cleaned = phone.replace(/\s+/g, '').replace(/^0/, '256');
    if (!/^256(7[0-9]|3[0-9])\d{7}$/.test(cleaned)) {
      setError('Please enter a valid Ugandan mobile number (e.g. 0700 000 000).');
      return;
    }

    setLoading(true);
    setStep('waiting');

    try {
      const response = await fetch('/api/payments/mtn-momo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleaned, amount: plan.price, tier: plan.tier, currency: 'UGX' }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Payment initiation failed. Please try again.');
        setStep('failed');
      } else {
        setStep('success');
        onSuccess();
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
      setStep('failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setStep('form');
    setError('');
    setLoading(false);
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className={`bg-gradient-to-r ${plan.gradient} rounded-2xl p-6 mb-6 text-white`}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-xl">📱</span>
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">Mobile Money Payment</p>
            <p className="text-white font-700 text-lg">{plan.label} Plan</p>
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-3xl font-800">UGX {plan.price.toLocaleString()}</span>
          <span className="text-white/70 text-sm">/month</span>
        </div>
      </div>

      {/* Step: Form */}
      {step === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-600 text-foreground mb-2">
              Mobile Money Phone Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm font-medium">🇺🇬 +256</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07XX XXX XXX"
                className="w-full pl-20 pr-4 py-3 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                maxLength={13}
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              MTN MoMo or Airtel Money number registered to your SIM
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <span className="text-red-500 text-sm mt-0.5">⚠️</span>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-600 text-foreground">How it works:</p>
            <div className="space-y-1.5">
              {[
                'Enter your MTN MoMo or Airtel Money number',
                'You\'ll receive a payment prompt on your phone',
                'Approve the payment with your PIN',
                'Your subscription activates instantly',
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs font-700 text-primary bg-primary/10 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-xs text-muted-foreground">{s}</p>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3.5 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60`}
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processing…
              </>
            ) : (
              <>
                <span>📲</span>
                Pay UGX {plan.price.toLocaleString()}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to plans
          </button>
        </form>
      )}

      {/* Step: Waiting */}
      {step === 'waiting' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Waiting for payment…</h3>
            <p className="text-sm text-muted-foreground">Check your phone and approve the mobile money prompt.</p>
          </div>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Payment Confirmed! 🎉</h3>
            <p className="text-sm text-muted-foreground">Your Rafeeq subscription is now active.</p>
          </div>
          <Link
            href="/dashboard"
            className={`block w-full py-3 px-6 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center`}
          >
            Go to Dashboard →
          </Link>
        </div>
      )}

      {/* Step: Failed */}
      {step === 'failed' && (
        <div className="text-center space-y-6 py-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Payment Failed</h3>
            <p className="text-sm text-muted-foreground">{error || 'Payment was not completed. Please try again.'}</p>
          </div>
          <button
            onClick={handleRetry}
            className={`w-full py-3 px-6 bg-gradient-to-r ${plan.gradient} text-white font-600 rounded-xl hover:opacity-90 transition-opacity`}
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}

export default function UgandaPricingPage() {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const handleSuccess = () => {
    setPaymentSuccess(true);
    setSelectedPlan(null);
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-800 text-foreground mb-2">Welcome to Rafeeq Premium! 🎉</h1>
            <p className="text-muted-foreground">Your subscription is now active. Start your growth journey.</p>
          </div>
          <Link
            href="/dashboard"
            className="block w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-600 rounded-xl hover:opacity-90 transition-opacity text-center"
          >
            Go to Dashboard →
          </Link>
        </div>
      </div>
    );
  }

  if (selectedPlan) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <button
            onClick={() => setSelectedPlan(null)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to plans
          </button>
          <UgandaCheckout
            plan={selectedPlan}
            onSuccess={handleSuccess}
            onCancel={() => setSelectedPlan(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-700 px-4 py-1.5 rounded-full text-sm font-600 mb-4">
            <span>🇺🇬</span>
            <span>Pay with Mobile Money · Uganda</span>
          </div>
          <h1 className="text-3xl font-800 text-foreground mb-3">Choose Your Rafeeq Plan</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Unlock AI-powered coaching, voice chat, and daily growth tools. Pay securely with MTN MoMo or Airtel Money — no card needed.
          </p>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {PLANS.map((plan) => (
            <div
              key={plan.tier}
              className={`relative bg-card border rounded-2xl overflow-hidden transition-all hover:shadow-lg ${
                plan.badge ? 'border-blue-300 shadow-blue-100 shadow-md' : 'border-border'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-700 px-3 py-1 rounded-bl-xl">
                  {plan.badge}
                </div>
              )}

              <div className={`bg-gradient-to-r ${plan.gradient} p-6 text-white`}>
                <h3 className="text-lg font-700 mb-1">{plan.label}</h3>
                <p className="text-white/70 text-xs mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-800">UGX {plan.price.toLocaleString()}</span>
                  <span className="text-white/70 text-sm">/month</span>
                </div>
              </div>

              <div className="p-6">
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setSelectedPlan(plan)}
                  className={`w-full py-3 px-4 bg-gradient-to-r ${plan.gradient} text-white font-600 text-sm rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}
                >
                  <span>📲</span>
                  Pay with Mobile Money
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Trust indicators */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { icon: '🔒', title: 'Secure Payments', desc: 'MTN MoMo & Airtel Money supported' },
              { icon: '⚡', title: 'Instant Activation', desc: 'Subscription active within seconds' },
              { icon: '📞', title: 'No Card Needed', desc: 'Pay directly from your mobile wallet' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2">
                <span className="text-2xl">{item.icon}</span>
                <p className="text-sm font-600 text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Prices shown in Ugandan Shillings (UGX). Subscriptions renew monthly. Cancel anytime.
        </p>

        <div className="text-center mt-4">
          <Link href="/pricing-kenya" className="text-xs text-primary hover:underline">
            Paying from Kenya? View KES pricing →
          </Link>
        </div>
      </div>
    </div>
  );
}
